import type { QueryClient } from '@tanstack/react-query';
import { type RouteObject } from 'react-router-dom';

import { firstAllowed, requirePermissions } from '@/app/requirePermissions';
import { PagePermissions } from '@/models/pagePermissions';
import { Permissions } from '@/models/permission';

const routes = (queryClient: QueryClient): RouteObject => ({
  path: 'store',
  loader: requirePermissions({ oneOf: PagePermissions.snapshots }),
  children: [
    {
      path: '',
      // A read-only viewer lands on the Snapshots list, not on the creation form
      // they cannot submit. 'snapshot' needs nothing beyond the store:read the
      // parent already checked, so this never 403s.
      loader: firstAllowed([
        { to: 'snapshot/new', oneOf: [Permissions.store.create] },
        { to: 'snapshot', oneOf: PagePermissions.snapshots },
      ]),
    },
    {
      path: 'snapshot',
      lazy: () => import('./snapshot'),
      children: [
        {
          path: 'new',
          // The gate is composed inside lazy(): a static `loader` alongside a lazy
          // one is ignored by the router, which would drop the form's own loader.
          lazy: async () => {
            const { loader, Component } = await import('./snapshot/form');
            return {
              loader: requirePermissions(
                { oneOf: [Permissions.store.create] },
                loader(queryClient),
              ),
              Component,
            };
          },
        },
        {
          path: ':snapshotId',
          lazy: async () => {
            const { loader, Component } = await import('./snapshot/form');
            return { loader: loader(queryClient), Component };
          },
        },
      ],
    },
    {
      path: 'bundles',
      lazy: () => import('./bundles'),
    },
  ],
});

export const storeRoute = (queryClient: QueryClient): RouteObject =>
  import.meta.env.VITE_ADMIN_DISABLED_FEATURE.includes('store')
    ? {}
    : routes(queryClient);
